/**
 * Party / group invite — host creates ONE shareable link with a guest limit;
 * guests self-claim personal passes at usebero.com/i/<code>. Wired:
 * POST /api/visitors/party, GET /party/mine, POST /party/:id (cancel).
 * MyGate-parity flagship; no design-bundle source — composed in app idiom.
 */
import { router } from 'expo-router';
import React from 'react';
import { Platform, Share, View } from 'react-native';

import { space } from '@bero/tokens';
import { num, str, useGateList } from '@/components/gate/api';
import { Segmented } from '@/components/gate/bits';
import { Icon } from '@/components/gate/icons';
import { useT } from '@/i18n';
import type { DictKey } from '@/i18n/en';
import { api } from '@/lib/client';
import { useTheme } from '@/theme';
import { AppText, Button, Card, Chip, EmptyState, Field, Input, Pill, Screen, SectionLabel, Skeleton, TopBar } from '@/ui';

const WHEN = ['Today', 'Tomorrow'];
const WHEN_LABEL: Record<string, DictKey> = { Today: 'pa_when_today', Tomorrow: 'pa_when_tomorrow' };
const LIMITS = [5, 10, 20, 50, 100];
const HOURS = [2, 4, 6, 8, 12];

function clock(d: Date): string {
  let h = d.getHours(); const am = h < 12 ? 'AM' : 'PM'; h = h % 12 || 12;
  return `${h}:${String(d.getMinutes()).padStart(2, '0')} ${am}`;
}

function parseClockTime(text: string): { h: number; m: number } | null {
  const match = text.trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (!match) return null;
  const h12 = parseInt(match[1], 10);
  const m = parseInt(match[2], 10);
  if (h12 < 1 || h12 > 12 || m < 0 || m > 59) return null;
  const isPM = match[3].toUpperCase() === 'PM';
  return { h: isPM ? (h12 % 12) + 12 : h12 % 12, m };
}

type InviteRow = {
  id: string; title: string; shareCode: string; claimed: number; limit: number;
  active: boolean; validUntil?: string;
};

function toInvite(v: unknown, i: number): InviteRow {
  return {
    id: str(v, 'id') ?? `pi${i}`,
    title: str(v, 'title') ?? 'Invite',
    shareCode: str(v, 'shareCode') ?? '',
    claimed: num(v, 'claimed') ?? 0,
    limit: num(v, 'guestLimit') ?? 0,
    active: (v as { isActive?: boolean })?.isActive !== false,
    validUntil: str(v, 'validUntil'),
  };
}

function inviteState(r: InviteRow): { value: string; label: DictKey; status: 'resolved' | 'pending' | 'rejected' } {
  if (!r.active) return { value: 'CANCELLED', label: 'pill_cancelled', status: 'pending' };
  if (r.validUntil && new Date(r.validUntil).getTime() < Date.now()) return { value: 'ENDED', label: 'pill_ended', status: 'pending' };
  if (r.claimed >= r.limit) return { value: 'FULL', label: 'pill_full', status: 'resolved' };
  return { value: 'OPEN', label: 'pill_open', status: 'resolved' };
}

type NavigatorShare = { share?: (data: { title?: string; text?: string; url?: string }) => Promise<void> };
type NavigatorClipboard = { clipboard?: { writeText?: (t: string) => Promise<void> } };

export default function PartyInviteScreen() {
  const { colors } = useTheme();
  const { t } = useT();
  const { items, state, reload } = useGateList<unknown>('/api/visitors/party/mine', 'invites');
  const rows = items.map(toInvite);

  const [title, setTitle] = React.useState('');
  const [when, setWhen] = React.useState('Today');
  const [timeFrom, setTimeFrom] = React.useState(() => clock(new Date()));
  const [hours, setHours] = React.useState(6);
  const [limit, setLimit] = React.useState(20);
  const [saving, setSaving] = React.useState(false);
  const [notice, setNotice] = React.useState<string | null>(null);
  const [noticeIsError, setNoticeIsError] = React.useState(false);
  const [cancellingId, setCancellingId] = React.useState<string | null>(null);
  const [copiedId, setCopiedId] = React.useState<string | null>(null);

  const create = async () => {
    const time = parseClockTime(timeFrom);
    if (!title.trim() || !time || saving) {
      if (!time) { setNotice(t('pi_time_error')); setNoticeIsError(true); }
      return;
    }
    setSaving(true);
    setNotice(null);
    const d = new Date();
    if (when === 'Tomorrow') d.setDate(d.getDate() + 1);
    d.setHours(time.h, time.m, 0, 0);
    try {
      await api.post<unknown>('/api/visitors/party', {
        title: title.trim(),
        guestLimit: limit,
        validFrom: d.toISOString(),
        validUntil: new Date(d.getTime() + hours * 3600 * 1000).toISOString(),
      });
      setTitle('');
      setNotice(t('pi_created'));
      setNoticeIsError(false);
      await reload();
    } catch {
      setNotice(t('pi_create_error'));
      setNoticeIsError(true);
    } finally {
      setSaving(false);
    }
  };

  const shareInvite = async (r: InviteRow) => {
    const link = `https://usebero.com/i/${r.shareCode}`;
    const text = t('pi_share_text', { title: r.title, link });
    if (Platform.OS !== 'web') {
      try { await Share.share({ message: text }); } catch { /* dismissed */ }
      return;
    }
    const nav = navigator as NavigatorShare & NavigatorClipboard;
    if (nav.share) {
      try { await nav.share({ title: r.title, text }); return; } catch { /* cancelled */ }
    }
    if (nav.clipboard?.writeText) {
      await nav.clipboard.writeText(link);
      setCopiedId(r.id);
      setTimeout(() => setCopiedId(null), 1600);
    }
  };

  const cancelInvite = async (r: InviteRow) => {
    setCancellingId(r.id);
    try {
      await api.post<unknown>(`/api/visitors/party/${encodeURIComponent(r.id)}`, { isActive: false });
      await reload();
    } catch {
      setNotice(t('pi_cancel_error'));
      setNoticeIsError(true);
    } finally {
      setCancellingId(null);
    }
  };

  return (
    <Screen scroll={false}>
      <TopBar title={t('pi_title')} onBack={() => router.back()} />
      <Screen padded style={{ paddingTop: space.s4, gap: space.s4 }}>
        <AppText variant="bodyMd" tone="secondary">
          {t('pi_intro')}
        </AppText>

        <Field label={t('pi_occasion')}>
          <Input value={title} onChangeText={setTitle} placeholder={t('pi_occasion_ph')} />
        </Field>
        <Field label={t('pi_when')}>
          <Segmented options={WHEN} labels={WHEN.map((w) => t(WHEN_LABEL[w]))} value={when} onChange={setWhen} />
        </Field>
        <Field label={t('pa_starts_at')}>
          <Input value={timeFrom} onChangeText={setTimeFrom} placeholder="6:00 PM" />
        </Field>
        <Field label={t('pi_open_for')}>
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: space.s2 }}>
            {HOURS.map((h) => (
              <Chip key={h} label={t('pa_n_hours', { n: h })} active={hours === h} onPress={() => setHours(h)} />
            ))}
          </View>
        </Field>
        <Field label={t('pi_guest_limit')}>
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: space.s2 }}>
            {LIMITS.map((l) => (
              <Chip key={l} label={t('pi_n_guests', { n: l })} active={limit === l} onPress={() => setLimit(l)} />
            ))}
          </View>
        </Field>
        {notice ? (
          <AppText variant="bodySm" tone={noticeIsError ? 'danger' : 'secondary'}>{notice}</AppText>
        ) : null}
        <Button
          title={title.trim() ? t('pi_create_named', { title: title.trim() }) : t('pi_create')}
          size="lg"
          loading={saving}
          disabled={!title.trim()}
          onPress={() => void create()}
        />

        <SectionLabel style={{ marginTop: space.s2 }}>{t('pi_your_invites')}</SectionLabel>
        {state === 'loading' ? (
          <View style={{ gap: space.s3 }}>{[0, 1].map((i) => <Skeleton key={i} h={88} />)}</View>
        ) : rows.length === 0 ? (
          <EmptyState
            icon={<Icon name="users" size={40} color={colors.textTertiary} />}
            title={t('pi_empty_title')}
            body={t('pi_empty_body')}
          />
        ) : (
          <View style={{ gap: space.s3 }}>
            {rows.map((r) => {
              const s = inviteState(r);
              const open = s.value === 'OPEN' || s.value === 'FULL';
              return (
                <Card key={r.id}>
                  <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                    <View style={{ flex: 1, minWidth: 0 }}>
                      <AppText variant="bodyMd" weight="500">{r.title}</AppText>
                      <AppText variant="bodySm" tone="tertiary" style={{ marginTop: 2 }}>
                        {t('pi_claimed', { n: r.claimed, total: r.limit })}
                      </AppText>
                    </View>
                    <Pill status={s.status} label={t(s.label)} />
                  </View>
                  {open && r.active ? (
                    <View style={{ flexDirection: 'row', gap: space.s2, marginTop: space.s3 }}>
                      <Button
                        title={copiedId === r.id ? t('pi_copied') : t('pi_share')}
                        kind="secondary"
                        style={{ flex: 1 }}
                        onPress={() => void shareInvite(r)}
                      />
                      <Button
                        title={cancellingId === r.id ? t('pi_cancelling') : t('cancel')}
                        kind="tertiary"
                        onPress={() => void cancelInvite(r)}
                      />
                    </View>
                  ) : null}
                </Card>
              );
            })}
          </View>
        )}
      </Screen>
    </Screen>
  );
}
